// Phase C — карточка для задачи-куска «теста сходимости».
//
// Видна ТОЛЬКО когда у задачи есть slice metadata (поле
// `slice_length_nominal_h !== null`). Сама карточка очень компактная
// — единственное, что оператор должен понять с одного взгляда:
// «это кусок 6.0h из суточного 24h, окно 00:00–06:00».
//
// Если реальная длина (по эпохам) расходится с номиналом более чем
// на 1% — добавляем annotation «(реально 5.3h)». Это Phase A's
// short-source case: 12h ask может прийти с 8h данных, и кривая
// сходимости должна это отражать в подписи точки.

import type { JobDetail } from '../api/types';

import './ConvergenceSliceCard.css';

interface Props {
  job: JobDetail;
}

/** Format an ISO datetime → "HH:MM" UTC. Slice windows are stored
 *  in UTC by the backend; we render in UTC because the convergence
 *  experiment is geometric (00:00 UTC anchor), not operator-local. */
function formatHm(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const h = String(d.getUTCHours()).padStart(2, '0');
  const m = String(d.getUTCMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

/** Format a length-in-hours with the trailing zero stripped:
 *  1.5h, 3h, 6h, 12h, 24h. */
function formatH(h: number): string {
  // ``Number.toString`` drops trailing zeros naturally; clamp the
  // long-decimal noise from gfzrnx-derived real lengths to one decimal.
  const rounded = Math.round(h * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export function ConvergenceSliceCard({ job }: Props) {
  if (job.slice_length_nominal_h == null) return null;
  const nominal = job.slice_length_nominal_h;
  const real = job.slice_length_real_h ?? nominal;
  // 1% tolerance — gfzrnx is exact to the epoch, so any meaningful
  // gap means the source was short.
  const isShort = Math.abs(real - nominal) / nominal > 0.01;
  const windowLabel = `${formatHm(job.slice_window_start)}–${formatHm(job.slice_window_end)}`;
  return (
    <div
      className="detail-block convergence-slice-block"
      data-testid="convergence-slice-card"
    >
      <div className="section-head">
        <span className="left">тест сходимости</span>
        <span className="right convergence-slice-length">
          {formatH(nominal)}h
          {isShort && (
            <span
              className="convergence-slice-real"
              data-testid="convergence-slice-real"
            >
              {' '}
              (реально {formatH(real)}h)
            </span>
          )}
        </span>
      </div>
      <div
        className="convergence-slice-meta"
        data-testid="convergence-slice-meta"
      >
        <span className="convergence-slice-window">окно {windowLabel} UTC</span>
        {job.tag && (
          <>
            <span className="convergence-slice-sep">·</span>
            <span className="convergence-slice-tag">{job.tag}</span>
          </>
        )}
      </div>
    </div>
  );
}
